import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { useEscapeLayer } from '../ui/Dialog';
import type { Placed, QuoteLine } from './types';

/** The note's text (owner 2026-10-23 20:25; 2026-10-24 11:03: short, under the line). */
export function placementText(p: Placed): string {
  if (p.kind === 'suggest') return `Należy do sekcji „${p.sectionName}”`;
  if (p.kind === 'category') return `Należy do kategorii „${p.categoryName}”`;
  // owner 2026-10-24 21:04 („dodano sekcje ""”): never empty quotes — the server always names the section it made
  // (the category's name is required), this only guards against a payload without it
  return p.sectionName.trim() ? `Dodano sekcję „${p.sectionName}” — pozycja trafiła tam` : 'Dodano nową sekcję — pozycja trafiła tam';
}

/**
 * Owner 2026-10-24 21:04 („ten napis ma zniknac po 3 sek.”): the note „Dodano sekcję …” closes by itself after this long.
 * Only that one: „Należy do sekcji / kategorii …” wait for the person's choice (Przenieś / Utwórz… / Zostaw).
 */
export const CREATED_NOTICE_MS = 3000;

/**
 * Owner 2026-10-24 11:03: under the line just added by "Dodaj…", in its row (as „brakuje N · Dopożycz”), blue, with
 * clickable words — no buttons. `suggest`: „Przenieś” moves the line to the end of its category's section and
 * `onMoved` selects it there. `category` (a section without a category, owner 11:02): „Utwórz sekcję i przenieś” makes
 * the category's section (catalog order) and moves the line in one request; the note becomes `created` (`onPlaced`).
 * `created`: „Cofnij” puts the line back where it was added and removes the new section while it is empty.
 * „Zostaw” / Esc / the next add closes it; `created` also closes by itself after `CREATED_NOTICE_MS` — not while the
 * pointer is over it or its „Cofnij” has the focus (the time starts again when they leave), nor while „Cofnij” works.
 */
export function PlacementNotice({
  placed,
  lineVersion,
  onClose,
  onMoved,
  onPlaced,
  onUndone,
}: {
  placed: Placed;
  /** The line's current version (review I2: not the one it had when added). */
  lineVersion: number;
  onClose: () => void;
  onMoved: (line: QuoteLine) => void;
  /** „Utwórz sekcję i przenieś” made the section: its „Cofnij” note (the screen reads the version again). */
  onPlaced: (next: Placed) => void;
  onUndone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEscapeLayer(onClose, true);
  const [held, setHeld] = useState({ pointer: false, focus: false });
  const autoHide = placed.kind === 'created' && !held.pointer && !held.focus && !busy && !error;
  // onClose is a new function on each render of the table: the time is not restarted by that
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!autoHide) return;
    const t = setTimeout(() => close.current(), CREATED_NOTICE_MS);
    return () => clearTimeout(t);
  }, [autoHide]);
  const move = async (sectionId: string) => api<QuoteLine>(`/api/quotes/lines/${placed.lineId}`, { method: 'PATCH', body: { sectionId, version: lineVersion } });
  async function run(work: () => Promise<boolean | void>) {
    setBusy(true);
    try {
      // `true`: the note stays (it became another one)
      if ((await work()) !== true) onClose();
      else setBusy(false);
    } catch (e) {
      setError(e instanceof ApiError ? (e.status === 409 ? 'Pozycja zmieniła się w międzyczasie — odśwież i przenieś ją ręcznie.' : e.message) : 'Nie udało się przenieść pozycji');
      setBusy(false);
    }
  }
  const link = (label: string, onClick: () => void, title?: string) => (
    <>
      {' · '}
      <button type="button" disabled={busy} title={title} onClick={onClick}>
        {label}
      </button>
    </>
  );
  const leave = link('Zostaw', onClose, 'Pozycja zostaje w tej sekcji');
  return (
    <span
      className="q-placement"
      role="status"
      data-testid="placement-notice"
      data-feature-id="quotes.category_sections"
      onPointerEnter={() => setHeld((h) => ({ ...h, pointer: true }))}
      onPointerLeave={() => setHeld((h) => ({ ...h, pointer: false }))}
      onFocus={() => setHeld((h) => ({ ...h, focus: true }))}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setHeld((h) => ({ ...h, focus: false }));
      }}
    >
      {placementText(placed)}
      {placed.kind === 'suggest' && (
        <>
          {link('Przenieś', () => void run(async () => onMoved(await move(placed.sectionId))), `Przenieś pozycję na koniec sekcji „${placed.sectionName}”`)}
          {leave}
        </>
      )}
      {placed.kind === 'category' && (
        <>
          {link(
            'Utwórz sekcję i przenieś',
            () =>
              void run(async () => {
                const line = await api<QuoteLine>(`/api/quotes/lines/${placed.lineId}/to-category-section`, { method: 'POST', body: { categoryId: placed.categoryId, version: lineVersion } });
                onMoved(line);
                if (!line.placement) return;
                onPlaced({ ...line.placement, lineId: line.id, lineName: line.name, lineVersion: line.version });
                return true;
              }),
            `Dodaj sekcję „${placed.categoryName}” w kolejności katalogu i przenieś do niej pozycję`,
          )}
          {leave}
        </>
      )}
      {placed.kind === 'created' &&
        link(
          'Cofnij',
          () =>
            void run(async () => {
              await move(placed.fromSectionId);
              // only an empty section goes: one with lines meanwhile stays (422 `section_not_empty`); other errors show
              await api(`/api/quotes/sections/${placed.sectionId}`, { method: 'DELETE' }).catch((e: unknown) => {
                if (!(e instanceof ApiError && e.code === 'section_not_empty')) throw e;
              });
              onUndone();
            }),
          'Przenieś pozycję z powrotem i usuń nową sekcję',
        )}
      {error && <span className="q-placement-error"> {error}</span>}
    </span>
  );
}
